import { useId, Fragment, useCallback, useState } from "react";
import ReferenceDisplay from "../../ui/content/ReferenceDisplay";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { getImportProposal, type ImportProposal, type ImportedDocument } from "../../api/content/importProposalsApi";
import { appPaths, entryNumberOfFragment } from "../../shared/routing/appPaths";
import { formatTimeAgo } from "../../shared/formatting/relativeTimes";
import { useLoad } from "../../shared/loading/useLoad";
import AppButton from "../../ui/buttons/AppButton";
import { ChangeCountsLine, ChangePageControls } from "./ChangePageControls";
import SegmentChangeList from "./SegmentChangeList";
import CorpusImpactReview from "./CorpusImpactReview";
import ReviewStateBadge from "../reviews/list/ReviewStateBadge";
import { useMemberNames } from "../threads/data/useMemberNames";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { useTabTitle } from "../../shell/workspace";

/**
 * `/import-proposals/<change set>`: a corpus import a Workflow proposed (Q-30). The curator
 * sees every document it adds, changes or removes, with its pages and segments, and what
 * it rests on: the source file by SHA-256 (the file stayed on the host's PC), the tool
 * version and its environment, the Thread entry of the run. Merging the head they saw is
 * the new corpus version; nothing is imported before.
 */
export default function ImportProposalPage() {
  const { t } = useTranslation();
  const { changeSetId = "" } = useParams();
  const load = useCallback((signal: AbortSignal) => getImportProposal(changeSetId, signal), [changeSetId]);
  const { state, reload } = useLoad(load);
  useTabTitle(state.status === "loaded" ? { kind: "other", name: state.value.changeSet.title } : null);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={reload} />;
  const value = state.value;
  return (
    <div className="mx-auto max-w-5xl space-y-4 py-4">
      <ImportProposalHeader value={value} />
      <ProvenanceCard value={value} />
      <DocumentsTable key={value.head} value={value} />
      <CorpusImpactReview key={value.head} value={value} onDecided={reload} />
    </div>
  );
}

function ImportProposalHeader({ value }: { value: ImportProposal }) {
  const { t, i18n } = useTranslation();
  const people = useMemberNames(value.changeSet.projectId);
  const changeSet = value.changeSet;
  return (
    <header className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="app-title flex-1 text-xl font-bold">{changeSet.title}</h2>
        <ReviewStateBadge state={changeSet.state} />
      </div>
      <p className="app-text-muted text-sm">
        {t("importProposals.page.into", { corpus: value.corpusName })} ·{" "}
        {t("reviews.page.proposed", { name: people.nameOf(changeSet.openedBy), time: formatTimeAgo(changeSet.openedAt, i18n.resolvedLanguage) })} ·{" "}
        <ReferenceDisplay kind="gitObject" value={value.head} copy={t("reviews.page.copyHead")} className="text-xs" />
      </p>
      {changeSet.originThreadEntries.map((reference) => {
        const [threadId = "", fragment = ""] = reference.split("#");
        return (
          <p key={reference} className="app-text-muted text-sm">
            {t("importProposals.page.madeIn")}{" "}
            <Link to={appPaths.thread(threadId, entryNumberOfFragment(fragment))} className="app-link underline">{t("reviews.page.threadEntry", { number: fragment })}</Link>
          </p>
        );
      })}
      {value.corpusMoved ? <p className="app-notice-warning rounded p-2 text-sm">{t("importProposals.page.corpusMoved")}</p> : null}
    </header>
  );
}

function ProvenanceCard({ value }: { value: ImportProposal }) {
  const { t } = useTranslation();
  const headingId = useId();
  const provenance = value.provenance;
  if (provenance === null) {
    return null;
  }
  return (
    <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">{t("importProposals.page.provenance")}</h3>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        {provenance.sourceFiles.map((file) => (
          <SourceFileRow key={file.input} label={t("importProposals.page.sourceFile")} path={file.path} sha256={file.sha256} sizeBytes={file.sizeBytes} />
        ))}
        <dt className="app-text-muted">{t("importProposals.page.tool")}</dt>
        <dd className="break-all font-mono text-xs">{provenance.tool}</dd>
        <dt className="app-text-muted">{t("importProposals.page.runtime")}</dt>
        <dd className="break-all text-xs">
          {provenance.runtime}
          {provenance.environmentDigest === null ? null : <span className="font-mono"> · {provenance.environmentDigest}</span>}
        </dd>
        <dt className="app-text-muted">{t("importProposals.page.importFile")}</dt>
        <dd className="break-all font-mono text-xs">{provenance.importSha256} · {provenance.mode}</dd>
      </dl>
      <p className="app-text-muted text-xs">{t("importProposals.page.fileStays")}</p>
    </section>
  );
}

function SourceFileRow({ label, path, sha256, sizeBytes }: { label: string; path: string; sha256: string; sizeBytes: number }) {
  const { i18n } = useTranslation();
  return (
    <>
      <dt className="app-text-muted">{label}</dt>
      <dd className="break-all text-xs">
        <span className="font-mono">{path}</span> · {new Intl.NumberFormat(i18n.resolvedLanguage).format(sizeBytes)} B · <span className="font-mono">{sha256}</span>
      </dd>
    </>
  );
}

function DocumentsTable({ value }: { value: ImportProposal }) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const numbers = new Intl.NumberFormat(i18n.resolvedLanguage);
  const merged = value.changeSet.state === "merged";
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const load = useCallback((signal: AbortSignal) => offset === 0 ? Promise.resolve(value) : getImportProposal(value.changeSet.changeSetId, signal, offset, value.head), [value, offset]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const page = state.value;
  const changedCount = page.counts.added + page.counts.modified + page.counts.removed;
  return (
    <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">{t("importProposals.page.documents", { count: changedCount })}</h3>
      {value.segmentationChanged ? <p className="text-sm">{t("importProposals.page.segmentationChanged")}</p> : null}
      <ChangeCountsLine counts={page.counts} />
      <div className="overflow-x-auto"><table className="w-full text-left text-sm">
        <thead className="app-text-muted text-xs">
          <tr>
            <th className="py-1 pr-2">{t("importProposals.page.columns.change")}</th>
            <th className="py-1 pr-2">{t("importProposals.page.columns.document")}</th>
            <th className="py-1 pr-2">{t("importProposals.page.columns.pages")}</th>
            <th className="py-1 pr-2 text-right">{t("importProposals.page.columns.characters")}</th>
            <th className="py-1 text-right">{t("importProposals.page.columns.segments")}</th>
          </tr>
        </thead>
        <tbody>
          {page.documents.map((document) => (
            <Fragment key={document.itemId}><tr className="border-t align-top">
              <td className="py-1 pr-2 text-xs">{t(`importProposals.changes.${document.change}`)}</td>
              <td className="py-1 pr-2">
                <DocumentName document={document} linked={merged && document.change !== "removed"} />
                <AppButton variant="secondary" size="sm" aria-expanded={expanded === document.itemId} onClick={() => { setExpanded(expanded === document.itemId ? null : document.itemId); }}>{t("importProposals.page.inspectSegments")}</AppButton>
              </td>
              <td className="py-1 pr-2 text-xs">{pagesOf(document)}</td>
              <td className="py-1 pr-2 text-right text-xs">{numbers.format(document.characters)}</td>
              <td className="py-1 text-right text-xs">{numbers.format(document.segments)}</td>
            </tr>
            {expanded === document.itemId ? <tr><td colSpan={5}><SegmentChangeList key={`${page.head}:${document.itemId}`} target={{ id: value.changeSet.changeSetId, itemId: document.itemId, head: page.head }} /></td></tr> : null}
            </Fragment>
          ))}
        </tbody>
      </table></div>
      <ChangePageControls offset={page.offset} limit={page.limit} nextOffset={page.nextOffset} total={changedCount} onPage={(next) => { setExpanded(null); setOffset(next); }} />
    </section>
  );
}

function DocumentName({ document, linked }: { document: ImportedDocument; linked: boolean }) {
  const title = document.metadata["section"] ?? "";
  const label = (
    <>
      <span className="font-mono text-xs">{document.key}</span>
      {title === "" ? null : <span className="ml-2">{title}</span>}
    </>
  );
  return linked ? <Link to={appPaths.sourceDocument({ documentId: document.itemId, version: null })} className="app-link">{label}</Link> : label;
}

/** "S. 28-39 (PDF 30-41)" from a converter's page metadata, or nothing. */
function pagesOf(document: ImportedDocument): string {
  const printed = document.metadata["printed_pages"] ?? "";
  const physical = document.metadata["pdf_pages"] ?? "";
  if (physical === "") {
    return "";
  }
  return printed === "" ? `PDF ${physical}` : `${printed} (PDF ${physical})`;
}
